import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';

import {
  Component,
  ChangeDetectionStrategy,
  ViewContainerRef,
  AfterViewInit,
  ChangeDetectorRef,
  OnDestroy,
  input,
  output,
  inject,
  viewChild,
  ComponentRef,
  Type,
  reflectComponentType
} from '@angular/core';

/**
 * Interface for cell configuration data
 */
interface CellConfig {
  data: {
    name: Type<any>;
    additionData?: boolean;
    aData?: any;
    fetch_key?: string;
    isClickable?: boolean;
  };
  dataProperty?: any;
}

/**
 * Interface for components that can be dynamically loaded into grid cells
 */
interface DynamicCellComponent {
  columnData?: any;
  dataProperty?: any;
  additionData?: any;
  isAllSelected?: any;
  keyName?: any;
  isClickable?: any;
  rowIndex?: any;
  selectedRows?: any;
  onAction?: Subject<any>;
  changeEvent?: Subject<any>;
}

@Component({
  selector: 'app-grid-cell',
  templateUrl: './grid-cell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: []
})
export class GridCellComponent implements AfterViewInit, OnDestroy {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  private subscription?: Subscription;

  private componentFactory: CellConfig | null = null;
  private componentRef: ComponentRef<DynamicCellComponent> | null = null;

  readonly cell = input(new BehaviorSubject<CellConfig | null>(null));
  readonly cellData = input(new BehaviorSubject<any>(null));
  readonly allSelection = input(new BehaviorSubject<boolean>(false));
  readonly rowIndex = input<number>();
  readonly selectedRows = input<any[]>();

  readonly container = viewChild('container', { read: ViewContainerRef });
  readonly actionEvent = output<any>();

  ngAfterViewInit(): void {
    this.loadComponent();
  }

  private loadComponent(): void {
    const containerRef = this.container();
    if (!containerRef) {
      console.warn('GridCellComponent: Container not found');
      return;
    }

    // combineLatest emits whenever any observable emits (after all have emitted at least once)
    this.subscription = combineLatest([
      this.cell(),
      this.cellData()
    ]).pipe(
      take(1), // Take only the first emission to avoid re-rendering
      takeUntil(this.destroy$)
    ).subscribe({
      next: ([cellConfig, cellData]) => {
        this.componentFactory = cellConfig;

        if (!cellConfig?.data?.name) {
          console.warn('GridCellComponent: Invalid cell configuration');
          return;
        }

        // Modern Angular 21 way: Use createComponent without ComponentFactoryResolver
        this.componentRef = containerRef.createComponent<DynamicCellComponent>(
          cellConfig.data.name
        );
        const instance = this.componentRef.instance;

        // Set inputs on the dynamically created component
        this.setComponentInputs(instance, cellConfig, cellData);

        // Subscribe to outputs from dynamically created component
        this.subscribeToComponentOutputs(instance);

        this.changeDetectorRef.detectChanges();
      },
      error: (error) => {
        console.error('GridCellComponent: Error loading component', error);
      }
    });
  }

  /**
   * Set inputs on dynamically created component instance
   * Handles both signal inputs and regular properties
   */
  private setComponentInputs(
    instance: DynamicCellComponent,
    cellConfig: CellConfig,
    cellData: any
  ): void {
    // Set columnData
    const cellDataSubject = new BehaviorSubject(cellData);
    this.setInputValue(instance, 'columnData', cellDataSubject);

    // Set dataProperty
    if (cellConfig?.dataProperty) {
      instance.dataProperty = cellConfig.dataProperty;
    }

    // Set additionData
    if (cellConfig.data.additionData) {
      const additionDataSubject = new BehaviorSubject(cellConfig.data.aData);
      this.setInputValue(instance, 'additionData', additionDataSubject);
    }

    // Set isAllSelected
    const allSelectionValue = this.allSelection();
    this.setInputValue(instance, 'isAllSelected', allSelectionValue);

    // Set keyName
    if (cellConfig.data.fetch_key) {
      const keyNameSubject = new BehaviorSubject(cellConfig.data.fetch_key);
      this.setInputValue(instance, 'keyName', keyNameSubject);
    }

    // Set isClickable
    if (cellConfig.data.isClickable) {
      const isClickableSubject = new BehaviorSubject(cellConfig.data.isClickable);
      this.setInputValue(instance, 'isClickable', isClickableSubject);
    }

    // Set rowIndex
    const rowIndexValue = this.rowIndex();
    if (rowIndexValue !== undefined) {
      this.setInputValue(instance, 'rowIndex', rowIndexValue);
    }

    // Set selectedRows
    const selectedRowsValue = this.selectedRows();
    if (selectedRowsValue) {
      this.setInputValue(instance, 'selectedRows', selectedRowsValue);
    }
  }

  /**
   * Helper method to set input values - handles both signal inputs and regular properties
   * Uses Angular's reflectComponentType for bulletproof signal input detection
   */
  private setInputValue(instance: any, propertyName: string, value: any): void {
    if (!this.componentRef) {
      return;
    }

    // CRITICAL FIX: Use reflectComponentType to safely detect signal inputs
    // This is the only bulletproof way to distinguish signal inputs from regular properties/methods
    try {
      const componentType = reflectComponentType(this.componentRef.componentType);

      if (componentType) {
        // Check if this property is defined as an input in the component metadata
        const inputMetadata = componentType.inputs.find(
          input => input.propName === propertyName
        );

        if (inputMetadata) {
          // It's a registered input - use setInput() (works for both signal and decorator inputs)
          this.componentRef.setInput(propertyName, value);
          return;
        }
      }
    } catch (error) {
      // If reflection fails, fall back to direct assignment
      console.warn(`GridCellComponent: Could not reflect component type for ${propertyName}`, error);
    }

    // Not an input or reflection failed - use direct assignment for regular properties
    if (instance[propertyName] !== undefined) {
      instance[propertyName] = value;
    }
  }

  /**
   * Subscribe to outputs from dynamically created component
   * Handles both Angular 21 output() and legacy EventEmitter
   */
  private subscribeToComponentOutputs(instance: DynamicCellComponent): void {
    if (instance.onAction) {
      try {
        // Angular 21 output() or EventEmitter - both have subscribe()
        // Check if it has pipe method (EventEmitter/Observable) vs OutputEmitterRef
        if (typeof (instance.onAction as any).pipe === 'function') {
          // It's an Observable/EventEmitter - use pipe with takeUntil
          (instance.onAction as any).pipe(
            takeUntil(this.destroy$)
          ).subscribe((data: any) => {
            this.actionEvent.emit(data);
          });
        } else {
          // It's an OutputEmitterRef (Angular 21) - subscribe directly
          // Note: OutputEmitterRef subscriptions are automatically cleaned up when component is destroyed
          (instance.onAction as any).subscribe((data: any) => {
            this.actionEvent.emit(data);
          });
        }
      } catch (error) {
        console.warn('GridCellComponent: Error subscribing to onAction', error);
      }
    }

    if (instance.changeEvent) {
      try {
        // Angular 21 output() or EventEmitter - both have subscribe()
        if (typeof (instance.changeEvent as any).pipe === 'function') {
          // It's an Observable/EventEmitter - use pipe with takeUntil
          (instance.changeEvent as any).pipe(
            takeUntil(this.destroy$)
          ).subscribe((data: any) => {
            this.actionEvent.emit(data);
          });
        } else {
          // It's an OutputEmitterRef (Angular 21) - subscribe directly
          (instance.changeEvent as any).subscribe((data: any) => {
            this.actionEvent.emit(data);
          });
        }
      } catch (error) {
        console.warn('GridCellComponent: Error subscribing to changeEvent', error);
      }
    }
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    // Clear container and destroy component
    const containerRef = this.container();
    if (containerRef) {
      containerRef.clear();
    }

    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }
}